/**
 * autoPrintController — resilient, self-scheduling auto-print worker
 * for the POS device.
 *
 * ============================================================================
 * DESIGN INVARIANTS
 * ============================================================================
 *   I1. printerService.printOrder() is NEVER called concurrently.
 *       Enforced by processingQueue mutex — one tick at a time.
 *   I2. The loop is self-scheduling via setTimeout(runLoop, delay). A
 *       single exception in tick() can NEVER kill future ticks: the
 *       scheduler lives in the finally of the outer catch.
 *   I3. Sequential FIFO drain — order N+1 never starts until N is
 *       PRINTED or (finally) FAILED after MAX_INLINE_RETRIES.
 *   I4. Backend PRINTING lock is acquired just-in-time (per-order),
 *       never batched.
 *   I5. Auth failures do NOT stop the loop — HTTP status is recorded
 *       in posDiagnostics and the next tick is scheduled as usual.
 *   I6. Independent from the sound worker — a chime failure cannot
 *       stop auto-print, and a print failure cannot stop the chime.
 *   I7. Reacts to visibility/focus/online events by triggering an
 *       immediate tick (see poke()), so WebView timer throttling
 *       during device sleep can't leave orders un-printed.
 * ============================================================================
 */
import axios from 'axios';
import printerService, { getMode as getPrinterMode } from './printerService';
import * as diag from '../pos/posDiagnostics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const POLL_INTERVAL_MS = 5 * 1000;
const MAX_INLINE_RETRIES = 3;
const RETRY_DELAY_MS = 800;
const PRINTER_SETTLE_DELAY_MS = 400;

// Module-level state — module is a true singleton, survives React
// re-renders / unmounts.
let stopped = true;
let running = false;              // set true while a loop iteration is running
let processingQueue = false;      // per-tick mutex (I1)
let scheduledTimer = null;        // outstanding setTimeout id
let printerSettingsRef = { auto_print: true };
let externalTicker = false;       // test-only

const log = (...args) => {
  // eslint-disable-next-line no-console
  console.log('[AUTO_PRINT]', ...args);
};

const authHeaders = () => {
  if (typeof localStorage === 'undefined') return null;
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : null;
};

const androidBridgePresent = () =>
  typeof window !== 'undefined' && !!window.AndroidPrinter;

const isEligible = () => {
  if (!authHeaders()) return false;
  if (getPrinterMode() !== 'android_builtin') return false;
  if (!androidBridgePresent()) return false;
  return true;
};

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// ---------------------------------------------------------------------------
// Backend calls — every one records HTTP status into diagnostics and never
// throws. On error the caller gets a sentinel value and the loop moves on.
// ---------------------------------------------------------------------------
const refreshSettings = async () => {
  const headers = authHeaders();
  if (!headers) return;
  try {
    const res = await axios.get(`${API_URL}/api/admin/printer-settings`, { headers });
    if (res.data) printerSettingsRef = res.data;
  } catch (_err) { /* keep last-known */ }
};

const fetchQueue = async () => {
  const headers = authHeaders();
  if (!headers) {
    diag.recordAutoPrintPoll({ httpStatus: 'NO_TOKEN', printableCount: 0 });
    return [];
  }
  try {
    const res = await axios.get(`${API_URL}/api/admin/orders/print-queue`, { headers });
    const orders = Array.isArray(res.data) ? res.data : [];
    diag.recordAutoPrintPoll({
      httpStatus: res.status,
      printableCount: orders.length,
      orderDetected: orders[0]?.id || null,
    });
    log(`Queue poll HTTP ${res.status}, printable=${orders.length}`);
    return orders;
  } catch (err) {
    const status = err?.response?.status || 'NETWORK_ERROR';
    diag.recordAutoPrintPoll({ httpStatus: status, printableCount: 0, error: err?.message });
    log(`Queue poll failed HTTP ${status}: ${err?.message}`);
    return [];
  }
};

const acquireLock = async (orderId) => {
  const headers = authHeaders();
  if (!headers) return null;
  try {
    const res = await axios.post(
      `${API_URL}/api/admin/orders/${orderId}/print-lock`,
      {},
      { headers },
    );
    return res.data;
  } catch (_err) {
    return null; // 409/other → skip this order for now
  }
};

const reportPrintResult = async (orderId, success, errorMessage) => {
  const headers = authHeaders();
  if (!headers) return;
  try {
    await axios.post(
      `${API_URL}/api/admin/orders/${orderId}/qz-print`,
      { success, error: errorMessage },
      { headers },
    );
  } catch (_err) { /* non-fatal — stale-lock TTL will reclaim */ }
};

// ---------------------------------------------------------------------------
// Per-order pipeline
// ---------------------------------------------------------------------------
const printWithRetries = async (order) => {
  let lastError = 'Print failed';
  for (let attempt = 1; attempt <= MAX_INLINE_RETRIES; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    const result = await printerService.printOrder(order).catch((e) => ({ success: false, error: e?.message }));
    if (result?.success) {
      await reportPrintResult(order.id, true);
      diag.recordPrintAttempt({ orderId: order.id, success: true });
      return { success: true };
    }
    lastError = (result?.error || 'Print failed').slice(0, 200);
    log(`Order ${order.id} attempt ${attempt}/${MAX_INLINE_RETRIES} failed: ${lastError}`);
    if (attempt < MAX_INLINE_RETRIES) {
      // eslint-disable-next-line no-await-in-loop
      await wait(RETRY_DELAY_MS);
    }
  }
  await reportPrintResult(order.id, false, lastError);
  diag.recordPrintAttempt({ orderId: order.id, success: false, error: lastError });
  // eslint-disable-next-line no-console
  console.error(`[AUTO_PRINT] Order ${order.id} FAILED after ${MAX_INLINE_RETRIES} attempts:`, lastError);
  return { success: false, error: lastError };
};

const processOrder = async (order) => {
  if (!order?.id) return { skipped: true };
  const locked = await acquireLock(order.id);
  if (!locked) { log(`Order ${order.id} lock unavailable — skipping`); return { skipped: true }; }
  log(`Printing order ${locked.id}`);
  return printWithRetries(locked);
};

// ---------------------------------------------------------------------------
// Tick — one drain of the print queue. Guarded by processingQueue so an
// externally-triggered poke() during an active drain becomes a no-op.
// ---------------------------------------------------------------------------
export const tick = async () => {
  if (!isEligible()) return { skipped: 'not-eligible' };
  if (processingQueue) { log('Tick skipped — previous worker still active'); return { skipped: 'busy' }; }
  processingQueue = true;
  const stats = { printed: 0, failed: 0, skipped: 0, total: 0 };
  try {
    await refreshSettings();
    if (printerSettingsRef?.auto_print === false) return { skipped: 'auto-print-off' };
    const queue = await fetchQueue();
    stats.total = queue.length;
    if (queue.length === 0) return stats;

    log(`Queue worker started — ${queue.length} order(s) pending`);
    for (let i = 0; i < queue.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const res = await processOrder(queue[i]);
      if (res.skipped) stats.skipped += 1;
      else if (res.success) stats.printed += 1;
      else stats.failed += 1;
      if (i < queue.length - 1) {
        log(`Waiting ${PRINTER_SETTLE_DELAY_MS}ms for printer to settle`);
        // eslint-disable-next-line no-await-in-loop
        await wait(PRINTER_SETTLE_DELAY_MS);
      }
    }
    log(`Queue worker completed — printed=${stats.printed} failed=${stats.failed} skipped=${stats.skipped}`);
    return stats;
  } finally {
    processingQueue = false;
  }
};

// ---------------------------------------------------------------------------
// Self-scheduling loop — the KEY reliability upgrade over setInterval.
// A single call to schedule() sets exactly one pending setTimeout. The
// runLoop finally-block re-schedules the next tick regardless of what
// happened. That combined with try/catch inside the loop means no
// possible failure mode can kill future ticks.
// ---------------------------------------------------------------------------
const runLoop = async () => {
  scheduledTimer = null;
  if (stopped) { running = false; return; }
  try {
    diag.setAutoPrintRunning(true);
    diag.setBridgeAvailable(androidBridgePresent());
    await tick();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[AUTO_PRINT] tick threw (unexpected):', err?.message || err);
  } finally {
    if (!stopped && !externalTicker) {
      scheduledTimer = setTimeout(runLoop, POLL_INTERVAL_MS);
    } else {
      running = false;
      diag.setAutoPrintRunning(false);
    }
  }
};

/**
 * Start the loop. Idempotent — repeated calls while already running are
 * no-ops. Never stops on its own; only stop() sets the stop flag.
 */
export const start = () => {
  if (running || externalTicker) return;
  stopped = false;
  running = true;
  diag.setAutoPrintRunning(true);
  runLoop();
};

/** Stop the loop. */
export const stop = () => {
  stopped = true;
  running = false;
  if (scheduledTimer) { clearTimeout(scheduledTimer); scheduledTimer = null; }
  diag.setAutoPrintRunning(false);
};

export const isRunning = () => running;

/**
 * Trigger an immediate tick without waiting for the scheduled interval.
 * Safe to call from visibilitychange / focus / online handlers.
 * If a tick is already running, this is a no-op (the mutex will bail).
 */
export const poke = () => {
  if (stopped || externalTicker) return;
  if (scheduledTimer) { clearTimeout(scheduledTimer); scheduledTimer = null; }
  // Kick the loop right away.
  runLoop();
};

// Test-only helpers
export const __testing__ = {
  reset: () => {
    stop();
    stopped = true;
    running = false;
    processingQueue = false;
    printerSettingsRef = { auto_print: true };
    externalTicker = false;
    if (scheduledTimer) { clearTimeout(scheduledTimer); scheduledTimer = null; }
  },
  useExternalTicker: (v) => { externalTicker = v; },
  isProcessing: () => processingQueue,
  runTick: () => tick(),
};

export default { start, stop, isRunning, tick, poke };
